/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 15/05/2025 13:50
 *
 */

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { ActionEvent, DialogType } from '@infodata-group/inf-commons';
import { TranslateModule } from '@ngx-translate/core';
import { DrawingCanvasComponent } from './drawing-canvas/drawing-canvas.component';
import { DrawingHeaderComponent } from './drawing-header/drawing-header.component';
import { DrawingMode } from '../../types/drawing/drawing.mode.type';
import { DialogComponent } from '@infodata-group/inf-commons-desktop';
import { DrawingHistory } from '../../types/drawing/drawing-history.type';

@Component({
  selector: 'infl-drawing',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DrawingHeaderComponent,
    DrawingCanvasComponent,
    DialogComponent,
  ],
  templateUrl: './drawing.component.html',
  styleUrl: './drawing.component.scss',
})
export class DrawingComponent {
  protected readonly DialogType = DialogType;

  public enableHistory = input<boolean>(true);
  public enableBackground = input<boolean>(true);
  public enableZoom = input<boolean>(true);
  public enableMove = input<boolean>(true);
  public fullScreen = input<boolean>(false);
  public showConfirmDialogOnCancel = input<boolean>(true);
  public history = input<DrawingHistory>([]);
  public headerDisplayed = input<boolean>(true);

  public color = input('#000000');
  private _color = signal('');
  public currentColor = computed(() => {
    return this._color() || this.color();
  });

  public height = input<number>(null);

  private isFullScreen = signal(false);
  protected isFullScreenComputed = computed(() => {
    return this.fullScreen() || this.isFullScreen();
  });

  public textCancel = input<string>('Annuler');
  public textSave = input<string>('Ok');

  public drawThickness = signal(4);
  public highlightThickness = signal(20);
  public eraserThickness = signal(30);
  public opacity = signal(1);

  public mode = signal<DrawingMode>('draw');

  protected computedThickness = computed(() => {
    switch (this.mode()) {
      case 'draw':
        return this.drawThickness();
      case 'highlight':
        return this.highlightThickness();
      case 'eraser':
        return this.eraserThickness();
    }
  });

  @ViewChild(DrawingHeaderComponent) header: DrawingHeaderComponent;
  @ViewChild(DrawingCanvasComponent) canvas: DrawingCanvasComponent;
  @ViewChild(DialogComponent) dialog: DialogComponent;

  public saveAction = output<string>();
  public cancelAction = output<void>();
  public historyChange = output<DrawingHistory>();

  protected onActionClickHeader($event: ActionEvent): void {
    switch ($event.action.name) {
      case 'draw':
        this.drawThickness.set($event.value);
        this.opacity.set(1);
        this.mode.set('draw');
        break;
      case 'color':
        this._color.set($event.value);
        break;
      case 'delete':
        this.clear();
        break;
      case 'highlight':
        this.highlightThickness.set($event.value);
        this.opacity.set(0.7);
        this.mode.set('highlight');
        break;
      case 'eraser':
        this.mode.set('eraser');
        this.eraserThickness.set($event.value);
        this.opacity.set(1);
        break;
      case 'save':
        this.saveAction.emit(this.getImage());
        break;
      case 'undo':
        this.canvas.undo();
        break;
      case 'redo':
        this.canvas.redo();
        break;
      case 'cancel':
        if (this.showConfirmDialogOnCancel()) {
          this.showConfirmDialogCancel();
          return;
        }
        this.cancelAction.emit();
        break;
      case 'fullScreen':
        this.isFullScreen.set(!this.isFullScreen());
        break;
      default:
        console.error(`Action not implemented: "${$event.action.name}"`);
        break;
    }
  }

  protected onDrawingChange($event: boolean) {
    if (!this.headerDisplayed()) {
      return;
    }
    if ($event) {
      this.header.clearFocusAction();
    }
  }

  private showConfirmDialogCancel() {
    this.dialog.open();
  }

  protected onActionDialog($event: string) {
    if ($event === 'confirm') {
      this.cancelAction.emit();
    }
  }

  protected onHistoryChange($event: DrawingHistory) {
    this.historyChange.emit($event);
  }

  public getImage(): string {
    return this.canvas.getImage();
  }

  public clear() {
    this.canvas.delete();
  }
}
